import { AircraftTag } from '@eflight/shared/dist/common/aircraft/AircraftTag';

import { type PerformanceProfile } from '../../Admin/Scheduling/model/PerformanceProfileTypes';
import { generateLabels } from '../../components/FormFields/FieldDetailedValue';
import { type AircraftCountableType, type AircraftUpliftableType } from '../CountableChanges/types';
import { type DbSaveable } from '../LocalDB/types';

// The enum lives in @eflight/shared (flight paperwork errors, also computed in the mcpServer lambda).
export { AircraftTag } from '@eflight/shared/dist/common/aircraft/AircraftTag';

export const AIRCRAFT_TAGS: AircraftTag[] = [
    AircraftTag.opsAto,
    AircraftTag.opsCat,
    AircraftTag.complex,
    AircraftTag.multiPilot,
    AircraftTag.mutiPilotCat,
    AircraftTag.multiEngine,
];

export const aircraftTagLabelsMap = new Map<AircraftTag, string>([
    [AircraftTag.complex, 'Complex'],
    [AircraftTag.multiEngine, 'ME'],
    [AircraftTag.multiPilot, 'Multi-pilot always'],
    [AircraftTag.mutiPilotCat, 'Multi-pilot in CAT'],
    [AircraftTag.opsAto, 'ATO'],
    [AircraftTag.opsCat, 'CAT'],
]);

export const aircraftTagLabels = generateLabels(aircraftTagLabelsMap);

export const AIRCRAFT_SPECIAL_APPROVAL_LVTO = 'LVTO';
export const AIRCRAFT_SPECIAL_APPROVAL_MNPS = 'MNPS';
export const AIRCRAFT_SPECIAL_APPROVAL_SET_IMC = 'SET-IMC';
export const AIRCRAFT_SPECIAL_APPROVAL_STEEP_APPROACH = 'Steep Approach';
export const AIRCRAFT_SPECIAL_APPROVAL_RLD = 'RLD';
export const AIRCRAFT_SPECIAL_APPROVAL_SHORT_LANDING_OPERATION = 'Short landing operation';
export const AIRCRAFT_SPECIAL_APPROVAL_RVSM = 'RVSM';
export const AIRCRAFT_SPECIAL_APPROVAL_RENTAL = 'Rental';
export const AIRCRAFT_SPECIAL_APPROVAL_CAT_II = 'CAT II';

export type AircraftSpecialApproval =
    | typeof AIRCRAFT_SPECIAL_APPROVAL_CAT_II
    | typeof AIRCRAFT_SPECIAL_APPROVAL_LVTO
    | typeof AIRCRAFT_SPECIAL_APPROVAL_MNPS
    | typeof AIRCRAFT_SPECIAL_APPROVAL_RENTAL
    | typeof AIRCRAFT_SPECIAL_APPROVAL_RLD
    | typeof AIRCRAFT_SPECIAL_APPROVAL_RVSM
    | typeof AIRCRAFT_SPECIAL_APPROVAL_SET_IMC
    | typeof AIRCRAFT_SPECIAL_APPROVAL_SHORT_LANDING_OPERATION
    | typeof AIRCRAFT_SPECIAL_APPROVAL_STEEP_APPROACH;

export const AIRCRAFT_SPECIAL_APPROVALS: AircraftSpecialApproval[] = [
    AIRCRAFT_SPECIAL_APPROVAL_LVTO,
    AIRCRAFT_SPECIAL_APPROVAL_MNPS,
    AIRCRAFT_SPECIAL_APPROVAL_SET_IMC,
    AIRCRAFT_SPECIAL_APPROVAL_STEEP_APPROACH,
    AIRCRAFT_SPECIAL_APPROVAL_RLD,
    AIRCRAFT_SPECIAL_APPROVAL_SHORT_LANDING_OPERATION,
    AIRCRAFT_SPECIAL_APPROVAL_RVSM,
    AIRCRAFT_SPECIAL_APPROVAL_RENTAL,
    AIRCRAFT_SPECIAL_APPROVAL_CAT_II,
];

export type Aircraft = DbSaveable & {
    id: string;
    registration: string;
    friendlyType: string;
    serialNumber: string;
    imageUrl?: string;
    countableTypes: AircraftCountableType[];
    upliftableTypes: AircraftUpliftableType[];
    specialApprovals: AircraftSpecialApproval[];
    tags: AircraftTag[];
    userId: string | undefined;
    managerInternalContactId?: string | null;
    aircraftTypeId: string | null;
    performanceProfile: PerformanceProfile | null;
    callsign: string | null;
    additionalTlbData: AircraftAdditionalTlbData;
};

export type AircraftAdditionalTlbData = {
    tachMeterOffset?: number;
};

export type AircraftForPicker = Pick<Aircraft, 'friendlyType' | 'id' | 'imageUrl' | 'registration' | 'serialNumber'>;
